import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';

import { formatDefaultSearchDate } from '~libs/dayjs';
import { useSearch } from '~libs/searchParams';

import { ClickableIcon, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { DataRow, TableView, useTableViewState } from '~components/table-view';

import { SectionItem } from '~support-at-home/models';

import budgetServiceColumns from '~support-at-home/forms/budget-services/list';
import { searchFields } from '~support-at-home/forms/budget-services/search';

import { getAll } from '~support-at-home/actions/budgetServices';

import { selectBudgetServices } from '~support-at-home/selectors/budgetServices';

interface ComponentProps {
  isActive: boolean;
}

const BudgetServices: React.FC<ComponentProps> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectBudgetServices);
  const { data, setData } = useTableViewState<DataRow<SectionItem>>(rows as DataRow<SectionItem>[]);

  const initialSearchState: any = {
    start_date: formatDefaultSearchDate(dayjs()),
  };

  const initialSortState: SortingState = [
    {
      id: 'start_date',
      desc: false,
    },
  ];

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('budget-services-search', initialSearchState);
  const [params, setParams] = useState({
    sort: initialSortState.map(({ id, desc }) => `${id}.${desc ? 'desc' : 'asc'}`),
    limit: 20,
  });

  useEffect(() => {
    if (isActive) {
      load();
    }
  }, [isActive]);

  const load = (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };

    // Keep search and load params separate as search can be cleared. Don't want to retain values when nothing is passed back.
    dispatch(getAll({ ...newParams, ...searchParams }));
    setParams(newParams);
  };

  const DISALLOWED_SORT_COLUMNS = ['section.budget.client.full_name'];

  const sort = (params: SortingState = []) => {
    if (!params.some((param) => DISALLOWED_SORT_COLUMNS.includes(param.id))) {
      load({ sort: params.map(({ id, desc }) => `${id}.${desc ? 'desc' : 'asc'}`) });
    }
  };

  useEffect(() => {
    load();
  }, [searchParams]);

  useEffect(() => {
    setData(rows as DataRow<SectionItem>[]);
  }, [rows]);

  const search = (values: { [key: string]: any }) => setSearchParams(values);

  return (
    <div>
      <Toolbar
        id="budget-services"
        rightCluster={
          <ClickableIcon
            className="me-2"
            icon={faMagnifyingGlass}
            id="invoice-search-button"
            onClick={() => setSearchBarOpen(!searchBarOpen)}
            toolTip="Toggle Search Bar"
          />
        }
      />
      <SearchBar fields={searchFields} onSubmit={search} initialValues={initialSearchState} isOpen={searchBarOpen} />
      <TableView
        id="budget-services"
        columns={budgetServiceColumns}
        data={data}
        allowSort={true}
        manualSort={true}
        onSort={sort}
      />
    </div>
  );
};

export default BudgetServices;
